import React from "react";
import Icon from "src/components/icon";
import { Sunny, Cloudy, Unknown, HalfSunny, Foggy, Sleet, Cloud, Snowing, Rain, Storm, NightPartCloud, NightClear } from "../components/maininfo/icons.weathersymbol";
import Avalanche1 from "src/components/icon/icons/avalancheRisk1";
import Avalanche2 from "src/components/icon/icons/avalancheRisk2";
import Avalanche3 from "src/components/icon/icons/avalancheRisk3";
import Avalanche4 from "src/components/icon/icons/avalancheRisk4";
import Avalanche5 from "src/components/icon/icons/avalancheRisk5";

// Wind Direction Icon Component
export interface WindDirectionIconProps {
    className?: string;
    orientation?: string;
    style?: React.CSSProperties;
    size?: number; // Default size, can be overridden by className
}

export function WindDirectionIcon({ className, orientation, style, size = 24 }: WindDirectionIconProps) {
    return (
        <svg 
            width={size} 
            height={size} 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg" 
            className={className}
            style={style}
        >
            <path 
                d="M12 19.6365V4.36377M12 4.36377L6 10.3638M12 4.36377L18 10.3638" 
                stroke="currentColor" 
                strokeWidth="1.96364" 
                strokeLinecap="round" 
                strokeLinejoin="round"
            />
            <title>{orientation}</title>
        </svg>
    );
}

// Wind Direction Rotation Angle Calculation
export function getWindRotationAngle(direction: string): number {
    const angles: { [key: string]: number } = {
        'S': 0, 'SSW': 22.5, 'SW': 45, 'WSW': 67.5,
        'W': 90, 'WNW': 112.5, 'NW': 135, 'NNW': 157.5,
        'N': 180, 'NNE': 202.5, 'NE': 225, 'ENE': 247.5,
        'E': 270, 'ESE': 292.5, 'SE': 315, 'SSE': 337.5
    };
    return angles[direction] || 0;
}

// Weather Symbol to Icon Component Mapping
export interface WeatherIconProps {
    weatherSymbol: string;
    className?: string;
    size?: number;
}

export function WeatherIcon({ weatherSymbol, className, size = 24 }: WeatherIconProps) {
    const iconProps = { className: className || `w-${size/4} h-${size/4}` };
    
    switch (weatherSymbol) {
        case "sunny": return <Sunny {...iconProps} />;
        case "halfsunny": return <HalfSunny {...iconProps} />;
        case "cloudy": return <Cloudy {...iconProps} />;
        case "foggy": return <Foggy {...iconProps} />;
        case "cloud": return <Cloud {...iconProps} />;
        case "sleet": return <Sleet {...iconProps} />;
        case "snowing": return <Snowing {...iconProps} />;
        case "rain": return <Rain {...iconProps} />;
        case "storm": return <Storm {...iconProps} />;
        case "nightpartcloud": return <NightPartCloud {...iconProps} />;
        case "nightclear": return <NightClear {...iconProps} />;
        default: return <Unknown {...iconProps} />;
    }
}

// Avalanche Risk Icon Component
export interface AvalancheRiskIconProps {
    risk: string;
    className?: string;
    size?: number;
}

export function AvalancheRiskIcon({ risk, className, size = 24 }: AvalancheRiskIconProps) {
    const iconProps = { className: className || `w-${size/4} h-${size/4}` };
    
    switch (risk) {
        case "1": return <Avalanche1 {...iconProps} />;
        case "2": return <Avalanche2 {...iconProps} />;
        case "3": return <Avalanche3 {...iconProps} />;
        case "4": return <Avalanche4 {...iconProps} />;
        case "5": return <Avalanche5 {...iconProps} />;
        default: return <Icon icon="AlertTriangle" {...iconProps} />;
    }
}

// Avalanche Risk Color Calculation
export function getAvalancheRiskColor(risk: string): string {
    switch (risk) {
        case "1": return '#31AC0D';
        case "2": return '#FABC1F';
        case "3": return '#E87E00';
        case "4": return '#E10003';
        case "5": return '#E10003';
        default: return '#A6A6AE';
    }
}

// Snow Period Label Mapping
export function getSnowPeriodLabel(period: string): string {
    switch (period) {
        case "12h": return "Snow 12 hours";
        case "24h": return "Snow 24 hours";
        case "48h": return "Snow 48 hours";
        case "72h": return "Snow 72 hours";
        case "week": return "Snow 7 days";
        case "season": return "Snow All season";
        case "base": return "Snow Base";
        default: return "Snow";
    }
}
